import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  DescriptionList,
  FormStack,
  Input,
  Page,
  Select,
  Text,
  Textarea,
} from "@marekl/adminer/components";
import { useConfirm, useT } from "@marekl/adminer/hooks";
import { apiErrorMessage } from "@marekl/adminer/api";
import { useAdminSeasons } from "../../../lib/queries/seasons";
import {
  useDeleteRuleSet,
  useSetRuleSetStatus,
  useUpdateRuleSet,
  type RuleSet,
  type RuleSetStatus,
} from "../../../lib/queries/rules";
import { RULESET_STATUS_LABELS, RULESET_STATUS_VARIANT } from "../../../lib/rulesLabels";

/**
 * The lifecycle of a rule set, with the reason each step exists. Mirrors
 * the transitions the backend allows; anything not listed here is
 * refused there too.
 */
const TRANSITIONS: Record<RuleSetStatus, { to: RuleSetStatus; label: string; hint: string }[]> = {
  draft: [
    {
      to: "active",
      label: "Aktivovat",
      hint: "Sada půjde použít v soutěžích. Aktivních sad může být víc naráz a dál se dají upravovat.",
    },
  ],
  active: [
    {
      to: "archived",
      label: "Archivovat",
      hint: "Sada se uzamkne. Archivovaná pravidla už nikdo nezmění, takže výsledky posouzené podle nich zůstanou dohledatelné.",
    },
  ],
  archived: [
    {
      to: "draft",
      label: "Vrátit mezi rozpracované",
      hint: "Vrátí sadu mezi rozpracované a znovu ji zpřístupní úpravám. Provede se záznam do audit logu.",
    },
  ],
};

export function SettingsTab({
  ruleSet,
  viewOnly = false,
}: {
  ruleSet: RuleSet;
  /** Čtení soutěží: the values shown, no edit, no status move, no delete. */
  viewOnly?: boolean;
}) {
  const confirm = useConfirm();
  const t = useT();
  const navigate = useNavigate();
  const { data: seasons = [] } = useAdminSeasons();
  const [name, setName] = useState(ruleSet.name);
  const [description, setDescription] = useState(ruleSet.description);
  const [seasonId, setSeasonId] = useState(ruleSet.season_id ? String(ruleSet.season_id) : "");
  // Music validation (Modul 6). Held as strings so the fields can be
  // emptied while typing; zero is a legitimate saved value for all three
  // ("no tolerance", "do not check"), so the parse below must not treat
  // it as absent.
  const [tolerance, setTolerance] = useState(String(ruleSet.music_tolerance_seconds));
  const [minBitrate, setMinBitrate] = useState(String(ruleSet.music_min_bitrate_kbps));
  const [minSampleRate, setMinSampleRate] = useState(String(ruleSet.music_min_sample_rate_hz));

  const update = useUpdateRuleSet(ruleSet.id);
  const setStatus = useSetRuleSetStatus(ruleSet.id);
  const remove = useDeleteRuleSet();

  const readOnly = viewOnly || ruleSet.status === "archived";
  const dirty =
    name !== ruleSet.name ||
    description !== ruleSet.description ||
    seasonId !== (ruleSet.season_id ? String(ruleSet.season_id) : "");

  const save = () =>
    update.mutate({
      name: name.trim(),
      description: description.trim(),
      season_id: seasonId ? seasonId : null,
    });

  const musicDirty =
    tolerance !== String(ruleSet.music_tolerance_seconds) ||
    minBitrate !== String(ruleSet.music_min_bitrate_kbps) ||
    minSampleRate !== String(ruleSet.music_min_sample_rate_hz);
  const musicValid = [tolerance, minBitrate, minSampleRate].every(
    (v) => v.trim() !== "" && Number.isFinite(Number(v)) && Number(v) >= 0,
  );

  const saveMusic = () =>
    update.mutate({
      // Name is required by the endpoint, so an unrelated save must send
      // the current one rather than blanking it.
      name: ruleSet.name,
      description: ruleSet.description,
      season_id: ruleSet.season_id ?? null,
      music_tolerance_seconds: Number(tolerance),
      music_min_bitrate_kbps: Number(minBitrate),
      music_min_sample_rate_hz: Number(minSampleRate),
    });

  return (
    <>
      <Page.SectionHeader title="Nastavení sady" />

      <Card>
        <Card.Header title="Základní údaje" />
        <Card.Content>
          {update.error && <Alert variant="err">{apiErrorMessage(update.error, t)}</Alert>}
          {readOnly ? (
            <DescriptionList>
              <DescriptionList.Item label="Název">{ruleSet.name}</DescriptionList.Item>
              <DescriptionList.Item label="Popis">
                {ruleSet.description || <Text variant="muted">—</Text>}
              </DescriptionList.Item>
              <DescriptionList.Item label="Domovská sezóna">
                {seasons.find((s) => s.id === ruleSet.season_id)?.code ?? "—"}
              </DescriptionList.Item>
            </DescriptionList>
          ) : (
            <FormStack>
              <Input
                label="Název sady"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <Textarea
                label="Popis"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <Select
                label="Domovská sezóna"
                value={seasonId}
                onChange={(e) => setSeasonId(e.target.value)}
              >
                <option value="">— bez sezóny —</option>
                {seasons.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.code}
                  </option>
                ))}
              </Select>
              <div>
                <Button
                  variant="primary"
                  loading={update.isPending}
                  disabled={!dirty || !name.trim()}
                  onClick={save}
                >
                  Uložit změny
                </Button>
              </div>
            </FormStack>
          )}
        </Card.Content>
      </Card>

      <Card>
        <Card.Header title="Kontrola hudby" />
        <Card.Content>
          <Text variant="muted">
            Platí pro celou sadu. Nahrávka mimo limit se vždy ohlásí; tolerance jen říká,
            jestli za odchylku hrozí srážka. Nula u bitrate nebo vzorkovací frekvence
            kontrolu vypne.
          </Text>
          {readOnly ? (
            <DescriptionList>
              <DescriptionList.Item label="Tolerance délky">
                {ruleSet.music_tolerance_seconds} s
              </DescriptionList.Item>
              <DescriptionList.Item label="Minimální bitrate">
                {ruleSet.music_min_bitrate_kbps} kbps
              </DescriptionList.Item>
              <DescriptionList.Item label="Minimální vzorkovací frekvence">
                {ruleSet.music_min_sample_rate_hz} Hz
              </DescriptionList.Item>
            </DescriptionList>
          ) : (
            <FormStack>
              <Input
                label="Tolerance délky (s)"
                type="number"
                min={0}
                value={tolerance}
                onChange={(e) => setTolerance(e.target.value)}
                hint="Odchylka, za kterou technický porotce nesráží."
              />
              <Input
                label="Minimální bitrate (kbps)"
                type="number"
                min={0}
                value={minBitrate}
                onChange={(e) => setMinBitrate(e.target.value)}
              />
              <Input
                label="Minimální vzorkovací frekvence (Hz)"
                type="number"
                min={0}
                value={minSampleRate}
                onChange={(e) => setMinSampleRate(e.target.value)}
              />
              <div>
                <Button
                  variant="primary"
                  loading={update.isPending}
                  disabled={!musicDirty || !musicValid}
                  onClick={saveMusic}
                >
                  Uložit kontrolu hudby
                </Button>
              </div>
            </FormStack>
          )}
        </Card.Content>
      </Card>

      {!viewOnly && (
        <Card>
          <Card.Header
            title="Stav sady"
            subtitle={
              <Badge variant={RULESET_STATUS_VARIANT[ruleSet.status]}>
                {RULESET_STATUS_LABELS[ruleSet.status]}
              </Badge>
            }
            subtitlePlacement="right"
          />
          <Card.Content>
            <FormStack>
              {/* Explanation first, action second — the same order as the
                  delete card below, so every button on this tab is preceded
                  by what it does. */}
              {TRANSITIONS[ruleSet.status].map((t) => (
                <div key={t.to}>
                  <div>
                    <Text variant="muted">{t.hint}</Text>
                  </div>
                  <Button
                    onClick={() =>
                      confirm({
                        title: t.label,
                        description: t.hint,
                        confirmLabel: t.label,
                        onConfirm: async () => {
                          await setStatus.mutateAsync(t.to);
                        },
                      })
                    }
                  >
                    {t.label}
                  </Button>
                </div>
              ))}
            </FormStack>
          </Card.Content>
        </Card>
      )}

      {ruleSet.status === "draft" && !viewOnly && (
        <Card>
          <Card.Header title="Smazat sadu" />
          <Card.Content>
            <FormStack>
              <Text variant="muted">
                Smazat lze jen rozpracovanou sadu. Aktivní a archivované sady se
                nemažou; historie se uchovává archivací.
              </Text>
              <div>
                <Button
                  // Outline — the confirm behind it carries the solid one.
                  variant="danger"
                  weight="outline"
                  onClick={() =>
                    confirm({
                      title: "Smazat sadu pravidel",
                      description: (
                        <>
                          Smaže se sada <strong>{ruleSet.name}</strong> se všemi tabulkami,
                          disciplínami a kombinacemi. Tuto akci nelze vzít zpět.
                        </>
                      ),
                      confirmLabel: "Smazat",
                      variant: "danger",
                      onConfirm: async () => {
                        await remove.mutateAsync(ruleSet.id);
                        navigate("/admin/pravidla");
                      },
                    })
                  }
                >
                  Smazat sadu pravidel
                </Button>
              </div>
            </FormStack>
          </Card.Content>
        </Card>
      )}


    </>
  );
}
